import { FormEvent, useState } from "react";

import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { WizardState } from "./types";

interface LocationStepProps {
  state: WizardState;
  busy: boolean;
  error?: string;
  onSubmit: (values: { yardPostcode: string; radiusMiles: number; defaultPrice: number }) => void;
}

const inputClasses =
  "block w-full rounded-md border-gray-300 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500";

export default function LocationStep({ state, busy, error, onSubmit }: LocationStepProps) {
  const [yardPostcode, setYardPostcode] = useState(state.yardPostcode);
  const [radiusMiles, setRadiusMiles] = useState(state.radiusMiles);
  const [defaultPrice, setDefaultPrice] = useState(state.defaultPrice);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({ yardPostcode: yardPostcode.trim(), radiusMiles, defaultPrice });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Where are you and how far do you collect?</h2>
        <p className="mt-1 text-sm text-gray-600">
          We'll use your yard's postcode and collection radius to work out which postcode districts you cover.
          You can fine-tune the list on the next step.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div>
          <label htmlFor="yardPostcode" className="block text-sm font-medium text-gray-700">
            Yard postcode
          </label>
          <input
            id="yardPostcode"
            className={`mt-1 uppercase ${inputClasses}`}
            value={yardPostcode}
            onChange={(e) => setYardPostcode(e.target.value)}
            placeholder="e.g. PL1 0LF"
            autoComplete="postal-code"
            required
          />
        </div>

        <div>
          <label htmlFor="radiusMiles" className="block text-sm font-medium text-gray-700">
            Collection radius: <span className="font-semibold">{radiusMiles} miles</span>
          </label>
          <input
            id="radiusMiles"
            type="range"
            min={1}
            max={100}
            step={1}
            value={radiusMiles}
            onChange={(e) => setRadiusMiles(+e.target.value)}
            className="mt-3 w-full accent-primary-600"
          />
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={200}
              value={radiusMiles}
              onChange={(e) => setRadiusMiles(Math.max(1, Math.min(200, +e.target.value || 1)))}
              className={`w-24 ${inputClasses}`}
              aria-label="Collection radius in miles"
            />
            <span className="text-sm text-gray-500">miles</span>
          </div>
        </div>

        <div>
          <label htmlFor="defaultPrice" className="block text-sm font-medium text-gray-700">
            Default collection price (£)
          </label>
          <div className="relative mt-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">£</span>
            <input
              id="defaultPrice"
              type="number"
              min={0}
              step={1}
              value={defaultPrice}
              onChange={(e) => setDefaultPrice(Math.max(0, +e.target.value || 0))}
              className={`pl-7 ${inputClasses}`}
              required
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Applied to every district to start with; you can change individual districts afterwards.
          </p>
        </div>
      </div>

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="flex justify-end">
        <ButtonInput isSubmit label={busy ? "Finding postcodes…" : "Find my postcodes"} disabled={busy || !yardPostcode.trim()} />
      </div>
    </form>
  );
}
